import { useEffect, useState, useCallback, useRef } from 'react';
import type { GameState, GameStateDiff, Player } from '@bangos/shared';
import { applyGameStateDiff } from '@bangos/shared';
import { socketManager } from './socket-manager';
import { useT } from './i18n';
import { LocaleSwitcher } from './i18n/LocaleSwitcher';
import { LandingScreen } from './components/LandingScreen';
import { InstallPrompt } from './components/InstallPrompt';
import { CreateRoomScreen } from './components/CreateRoomScreen';
import { JoinRoomScreen } from './components/JoinRoomScreen';
import { LobbyScreen } from './components/LobbyScreen';
import { GameScreen } from './components/GameScreen';
import './styles/gameplay.css';

type Screen =
  | { name: 'landing' }
  | { name: 'createRoom' }
  | { name: 'joinRoom' }
  | { name: 'lobby'; roomCode: string }
  | { name: 'game' };

/** Extract the room code from the URL query param, if present. */
function getRoomFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('room') ?? '';
}

function App() {
  const t = useT();
  const [connected, setConnected] = useState(false);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [initialRoomCode] = useState<string>(() => getRoomFromUrl());
  const [screen, setScreen] = useState<Screen>(() =>
    initialRoomCode ? { name: 'joinRoom' } : { name: 'landing' },
  );
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerId, setPlayerId] = useState<string>('');
  const [isReady, setIsReady] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconnectionNotification, setReconnectionNotification] = useState<string | null>(null);
  const [teamAssignment, setTeamAssignment] = useState<'blue' | 'red' | null>(null);

  // Keep GameState for future use (phase, scores etc)
  const [gameState, setGameStateRaw] = useState<GameState>({
    roomId: '',
    roomCode: '',
    round: 0,
    phase: 'lobby',
    activeTeam: null,
    currentRound: null,
    roundHistory: [],
    submittedSpectra: [],
    blueScore: 0,
    redScore: 0,
    totalRounds: 8,
    creatorId: '',
    teams: { blue: [], red: [] },
  });

  // Refs to latest state so event handlers (registered once) always see current values
  const playerIdRef = useRef(playerId);
  playerIdRef.current = playerId;
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  const handleGameStateSync = useCallback((payload: unknown) => {
    const state = payload as GameState;
    console.log('[App] received game_state_sync:', state);
    setGameStateRaw((prev) => {
      // Shallow merge to preserve object identity for unchanged sub-objects,
      // helping React.memo work effectively on child components.
      // Deep-merge currentRound if it changed to keep sub-object references stable.
      if (prev.currentRound && state.currentRound) {
        return {
          ...prev,
          ...state,
          currentRound: { ...prev.currentRound, ...state.currentRound },
        };
      }
      return { ...prev, ...state };
    });
  }, []);

  const handleGameStateDiff = useCallback((diff: unknown) => {
    const stateDiff = diff as GameStateDiff;
    console.log('[App] received game_state_diff:', stateDiff);
    setGameStateRaw((prev) => applyGameStateDiff(prev, stateDiff));
  }, []);

  // Reconnect with stored identity on mount (only once, no dependencies).
  // Only auto-reconnect if the URL explicitly has a ?room= param, meaning
  // the user navigated directly to a room. Otherwise, start fresh.
  const reconnectedRef = useRef(false);
  useEffect(() => {
    if (reconnectedRef.current) return;
    reconnectedRef.current = true;

    const identity = socketManager.getStoredIdentity();
    if (identity && initialRoomCode && !socketManager.connected) {
      console.log('[App] stored identity found, reconnecting...', identity);
      setConnectionAttempted(true);
      socketManager.connectToRoom();
    }
  }, []);

  // Register event handlers (runs once, handlers reference latest state via closures)
  useEffect(() => {
    socketManager.onGameStateSync(handleGameStateSync);
    socketManager.onGameStateDiff(handleGameStateDiff);

    // Use centralized connection state tracking
    socketManager.onConnectionStateChange((isConnected) => {
      setConnected(isConnected);
      if (isConnected) {
        console.log('[App] connected, socketId:', socketManager.getSocketId());
      } else {
        console.log('[App] disconnected');
      }
    });

    const onConnect = () => {
      // Handled by onConnectionStateChange
    };

    const onDisconnect = () => {
      // Handled by onConnectionStateChange
    };

    const onRoomCreated = (payload: { roomId: string; roomCode: string; playerId: string }) => {
      socketManager.storeIdentity(payload.playerId, payload.roomCode);
      setPlayerId(payload.playerId);
      setIsHost(true);
      setIsReady(false);
      setTeamAssignment(null);
      // Replace the optimistically-added temp player with the real server-assigned ID.
      // If the server sends a room_state later, it will overwrite this anyway.
      setPlayers((prev) => {
        // If we already have a temp player, just update its ID
        const tempPlayer = prev.find((p) => p.id === 'temp');
        if (tempPlayer) {
          return prev.map((p) =>
            p.id === 'temp' ? { ...p, id: payload.playerId } : p,
          );
        }
        // Otherwise, start fresh (shouldn't normally happen)
        return [
          {
            id: payload.playerId,
            name: '',
            team: null,
            isConnected: true,
            isReady: false,
            role: 'player' as const,
          },
        ];
      });
      setGameStateRaw((prev) => ({
        ...prev,
        roomId: payload.roomId,
        roomCode: payload.roomCode,
        creatorId: payload.playerId,
      }));
      setScreen({ name: 'lobby', roomCode: payload.roomCode });
      setError(null);
    };

    const onRoomJoined = (payload: { roomId: string; roomCode: string; playerId: string }) => {
      socketManager.storeIdentity(payload.playerId, payload.roomCode);
      setPlayerId(payload.playerId);
      setIsHost(false);
      setIsReady(false);
      setTeamAssignment(null);
      setGameStateRaw((prev) => ({
        ...prev,
        roomId: payload.roomId,
        roomCode: payload.roomCode,
      }));
      setScreen({ name: 'lobby', roomCode: payload.roomCode });
      setError(null);
    };

    const onPlayerJoined = (payload: {
      playerId: string;
      displayName: string;
      role?: 'player' | 'spectator';
    }) => {
      setPlayers((prev) => {
        // Don't add duplicates
        if (prev.some((p) => p.id === payload.playerId)) return prev;
        return [
          ...prev,
          {
            id: payload.playerId,
            name: payload.displayName,
            team: null,
            isConnected: true,
            isReady: false,
            role: payload.role ?? 'player',
          },
        ];
      });
    };

    const onPlayerLeft = (payload: { playerId: string; displayName: string }) => {
      setPlayers((prev) => prev.filter((p) => p.id !== payload.playerId));
      // If leaving player was the host, we may need to update
      if (payload.playerId === gameStateRef.current.creatorId) {
        setIsHost(false);
      }
    };

    const onPlayerReady = (payload: { playerId: string; isReady: boolean }) => {
      setPlayers((prev) =>
        prev.map((p) => (p.id === payload.playerId ? { ...p, isReady: payload.isReady } : p)),
      );
    };

    const onTeamAssigned = (payload: { playerId: string; team: 'blue' | 'red' }) => {
      if (payload.playerId === playerIdRef.current) {
        setTeamAssignment(payload.team);
      }
      setPlayers((prev) =>
        prev.map((p) => (p.id === payload.playerId ? { ...p, team: payload.team } : p)),
      );
    };

    const onGameStarted = (payload: { roomId: string; gameState: unknown }) => {
      console.log('[App] game started:', payload);
      const gs = payload.gameState as GameState;
      setGameStateRaw(gs);
      // Update player teams from the game state
      if (gs.teams) {
        setPlayers((prev) =>
          prev.map((p) => ({
            ...p,
            team: gs.teams.blue.includes(p.id)
              ? 'blue'
              : gs.teams.red.includes(p.id)
                ? 'red'
                : p.team,
          })),
        );
      }
      // Transition to game screen
      setScreen({ name: 'game' });
    };

    const onError = (payload: { code: string; message: string }) => {
      console.log('[App] server error:', payload);
      // Automatic reconnection failures with stale identity should not
      // surface as user-facing errors — just clear the identity silently.
      // Also suppress errors from stale join_room with empty displayName.
      if (
        payload.code === 'RECONNECT_FAILED' ||
        payload.code === 'JOIN_ROOM_ERROR' ||
        payload.code === 'INTERNAL_ERROR'
      ) {
        socketManager.clearIdentity();
        return;
      }
      setError(payload.message);
    };

    const onPlayerReconnected = (payload: { playerId: string; displayName: string }) => {
      console.log('[App] player reconnected:', payload.displayName);
      setPlayers((prev) =>
        prev.map((p) => (p.id === payload.playerId ? { ...p, isConnected: true } : p)),
      );
      setReconnectionNotification(`${payload.displayName} has reconnected`);
      setTimeout(() => setReconnectionNotification(null), 5000);
    };

    socketManager.on('connect', onConnect);
    socketManager.on('disconnect', onDisconnect);
    socketManager.on('room_created', onRoomCreated);
    socketManager.on('room_joined', onRoomJoined);
    socketManager.on('player_joined', onPlayerJoined);
    socketManager.on('player_left', onPlayerLeft);
    socketManager.on('player_ready', onPlayerReady);
    const onClueSubmitted = (payload: { playerId: string; text: string; clueId: string }) => {
      console.log('[App] clue_submitted:', payload);
      setGameStateRaw((prev) => {
        if (!prev.currentRound) return prev;
        // Avoid adding duplicate clues
        if (prev.currentRound.clues.some((c) => c.id === payload.clueId)) {
          return prev;
        }
        return {
          ...prev,
          phase: 'guessing',
          currentRound: {
            ...prev.currentRound,
            clues: [
              ...prev.currentRound.clues,
              {
                id: payload.clueId,
                playerId: payload.playerId,
                text: payload.text,
                position: -1,
              },
            ],
          },
        };
      });
    };

    const onGuessSubmitted = (payload: { playerId: string; position: number; guessId: string }) => {
      console.log('[App] guess_submitted:', payload);
      setGameStateRaw((prev) => {
        if (!prev.currentRound) return prev;
        // Avoid adding duplicate guesses
        if (prev.currentRound.guesses.some((g) => g.id === payload.guessId)) {
          return prev;
        }
        return {
          ...prev,
          phase: 'reveal',
          currentRound: {
            ...prev.currentRound,
            guesses: [
              ...prev.currentRound.guesses,
              {
                id: payload.guessId,
                playerId: payload.playerId,
                position: payload.position,
              },
            ],
          },
        };
      });
    };

    /**
     * Handle phase_changed events from the server.
     * This ensures the client updates the game phase immediately,
     * without waiting for the game_state_sync message.
     */
    const onPhaseChanged = (payload: {
      phase: 'lobby' | 'psychicTurn' | 'guessing' | 'reveal' | 'gameOver';
      round: number;
      activeTeam: 'blue' | 'red';
    }) => {
      console.log('[App] phase_changed:', payload);
      setGameStateRaw((prev) => ({
        ...prev,
        phase: payload.phase,
        round: payload.round,
        activeTeam: payload.activeTeam,
      }));
    };

    socketManager.on('team_assigned', onTeamAssigned);
    socketManager.on('game_started', onGameStarted);
    socketManager.on('clue_submitted', onClueSubmitted);
    socketManager.on('guess_submitted', onGuessSubmitted);
    socketManager.on('phase_changed', onPhaseChanged);
    socketManager.on('error', onError);

    /**
     * Handle room_state event sent by the server on reconnect.
     * Restores the full UI state: player list, screen, host/ready/team status.
     */
    const onRoomState = (payload: {
      roomCode: string;
      players: Player[];
      playerId: string;
      isHost: boolean;
      isReady: boolean;
      teamAssignment: 'blue' | 'red' | null;
      phase: string;
    }) => {
      console.log('[App] received room_state:', payload);
      setPlayerId(payload.playerId);
      setPlayers(payload.players);
      setIsHost(payload.isHost);
      setIsReady(payload.isReady);
      setTeamAssignment(payload.teamAssignment);

      // Determine which screen to show based on the phase sent by the server
      if (payload.phase === 'lobby') {
        setScreen({ name: 'lobby', roomCode: payload.roomCode });
      } else {
        setScreen({ name: 'game' });
      }
      setError(null);
    };

    const onRematchStarted = (payload: {
      roomId: string;
      roomCode: string;
      gameState: GameState;
    }) => {
      console.log('[App] rematch_started:', payload);
      // Update game state with the reset state from the server
      setGameStateRaw(payload.gameState);
      // Reset local state for the lobby
      setIsReady(false);
      setTeamAssignment(null);
      // Update player team assignments from the new teams
      setPlayers((prev) =>
        prev.map((p) => ({
          ...p,
          team: payload.gameState.teams.blue.includes(p.id)
            ? 'blue'
            : payload.gameState.teams.red.includes(p.id)
              ? 'red'
              : null,
          isReady: false,
        })),
      );
      // Transition back to the lobby screen, staying in the same room
      setScreen({ name: 'lobby', roomCode: payload.roomCode });
      setError(null);
    };

    const onSpectrumSubmitted = (payload: {
      playerId: string;
      left: string;
      right: string;
      spectrumId: string;
    }) => {
      console.log('[App] spectrum_submitted:', payload);
      setGameStateRaw((prev) => {
        // Avoid adding duplicate spectra
        if (prev.submittedSpectra.some((s) => s.id === payload.spectrumId)) {
          return prev;
        }
        return {
          ...prev,
          submittedSpectra: [
            ...prev.submittedSpectra,
            {
              id: payload.spectrumId,
              left: payload.left,
              right: payload.right,
            },
          ],
        };
      });
    };

    socketManager.on('spectrum_submitted', onSpectrumSubmitted);
    socketManager.on('player_reconnected', onPlayerReconnected);
    socketManager.on('rematch_started', onRematchStarted);
    socketManager.on('room_state', onRoomState);

    // No cleanup — handlers stay registered for the app lifetime.
    // The socketManager is a singleton; disconnecting in cleanup would
    // break StrictMode double-mount in dev (disconnect + reconnect loop).
    // handleLeaveRoom is responsible for explicit disconnect.
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const handleCreateRoom = useCallback(async (displayName: string) => {
    setError(null);

    try {
      // 1. Create the room via REST API
      const { roomCode } = await socketManager.createRoomViaRest();
      console.log('[App] room created:', roomCode);

      // Update URL with room code
      const url = new URL(window.location.href);
      url.searchParams.set('room', roomCode);
      window.history.replaceState({}, '', url.toString());

      // Add the creator as a player immediately for optimistic UI
      setPlayers([
        {
          id: 'temp',
          name: displayName,
          team: null,
          isConnected: true,
          isReady: false,
          role: 'player' as const,
        },
      ]);

      // 2. Disconnect any stale connection (e.g. from stored-identity reconnect),
      //    then connect to the new room.
      socketManager.disconnect();
      socketManager.clearIdentity();
      socketManager.connectToRoom(roomCode, { displayName });
    } catch (err) {
      console.error('[App] Failed to create room:', err);
      setError('Failed to create room. Please try again.');
    }
  }, []);

  const handleJoinRoom = useCallback(
    (roomCode: string, displayName: string, asSpectator?: boolean) => {
      setError(null);

      // Update URL with room code
      const url = new URL(window.location.href);
      url.searchParams.set('room', roomCode);
      window.history.replaceState({}, '', url.toString());

      // Disconnect any stale connection, then connect to the new room
      socketManager.disconnect();
      socketManager.clearIdentity();
      socketManager.connectToRoom(roomCode, { displayName, asSpectator });
    },
    [],
  );

  const handleLeaveRoom = useCallback(() => {
    if (socketManager.connected && (screen.name === 'lobby' || screen.name === 'game')) {
      socketManager.emit('leave_room', {
        roomCode: screen.name === 'lobby' ? screen.roomCode : gameState.roomCode,
      });
    }
    socketManager.disconnect();
    socketManager.clearIdentity();
    setPlayers([]);
    setPlayerId('');
    setIsHost(false);
    setIsReady(false);
    setTeamAssignment(null);
    setScreen({ name: 'landing' });
    setError(null);

    // Remove room from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('room');
    window.history.replaceState({}, '', url.toString());
  }, [screen, gameState.roomCode]);

  const handleToggleReady = useCallback(() => {
    if (!playerId) return;
    const newReady = !isReady;
    setIsReady(newReady);
    socketManager.emit('player_ready', { playerId, isReady: newReady });
  }, [playerId, isReady]);

  const handleStartGame = useCallback(() => {
    if (screen.name === 'lobby') {
      socketManager.emit('start_game', { roomCode: screen.roomCode });
    }
  }, [screen]);

  const handlePlayAgain = useCallback(() => {
    // Tell the server we want a rematch (the server will reset state and broadcast)
    socketManager.emit('play_again', { roomCode: gameState.roomCode });
  }, [gameState.roomCode]);

  const handleSubmitSpectrum = useCallback(
    (left: string, right: string) => {
      if (!connected) {
        setError('Not connected to server. Cannot submit spectrum.');
        return;
      }
      const roomCode =
        screen.name === 'lobby'
          ? screen.roomCode
          : screen.name === 'game'
            ? gameState.roomCode
            : null;
      if (!roomCode) {
        setError('Cannot submit spectrum outside of a game or lobby.');
        return;
      }
      setError(null);
      socketManager.emit('submit_spectrum', {
        roomCode,
        left,
        right,
      });
    },
    [connected, screen, gameState.roomCode],
  );

  const renderScreen = () => {
    switch (screen.name) {
      case 'landing':
        return (
          <LandingScreen
            onCreateRoom={() => setScreen({ name: 'createRoom' })}
            onJoinRoom={() => setScreen({ name: 'joinRoom' })}
          />
        );

      case 'createRoom':
        return (
          <CreateRoomScreen
            onCreateRoom={handleCreateRoom}
            error={error}
            clearError={clearError}
            onBack={() => {
              setError(null);
              setScreen({ name: 'landing' });
            }}
          />
        );

      case 'joinRoom':
        return (
          <JoinRoomScreen
            initialRoomCode={initialRoomCode}
            onJoinRoom={handleJoinRoom}
            error={error}
            clearError={clearError}
            onBack={() => {
              setError(null);
              setScreen({ name: 'landing' });
            }}
          />
        );

      case 'lobby':
        return (
          <LobbyScreen
            roomCode={screen.roomCode}
            players={players}
            isHost={isHost}
            playerCount={players.length}
            isReady={isReady}
            isSpectator={players.find((p) => p.id === playerId)?.role === 'spectator'}
            teamAssignment={teamAssignment}
            onToggleReady={handleToggleReady}
            onStartGame={handleStartGame}
            onLeaveRoom={handleLeaveRoom}
            onSubmitSpectrum={handleSubmitSpectrum}
          />
        );

      case 'game':
        return (
          <GameScreen
            gameState={gameState}
            playerId={playerId}
            isSpectator={players.find((p) => p.id === playerId)?.role === 'spectator'}
            players={players}
            onPlayAgain={handlePlayAgain}
            onSubmitSpectrum={handleSubmitSpectrum}
          />
        );
    }
  };

  return (
    <div className="app-wrapper">
      {/* Connection Lost — Reconnecting... overlay */}
      {!connected && connectionAttempted && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            padding: '0.75rem 1rem',
            backgroundColor: 'rgba(255, 193, 7, 0.95)',
            color: '#856404',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '0.95rem',
            borderBottom: '2px solid #ffc107',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
          role="alert"
        >
          <span style={{ fontSize: '1.2rem' }}>&#9888;</span>
          <span>{t('app.connectionLost')}</span>
        </div>
      )}
      {/* disable for now */}
      {/* <InstallPrompt /> */}
      <LocaleSwitcher />
      {reconnectionNotification && (
        <div
          style={{
            marginTop: '0',
            marginBottom: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            border: '1px solid #c3e6cb',
          }}
        >
          {reconnectionNotification}
        </div>
      )}
      {renderScreen()}
    </div>
  );
}

export default App;
